import type { DraftState, PlannedOrderSettings, SettingChange, WizardFile, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { TargetTaxonomyKey } from '../../Generated/TargetTaxonomy.ts'
import type { ProcessingTypeKey } from '../../Generated/ProcessingType.ts'
import type { TargetRegionKey } from '../../Generated/TargetRegion.ts'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.ts'
import { withoutInFlightWrites } from './draftStorage.ts'
import { regionOf } from './orderSettingsPlan.ts'

/** What the server says about a draft's order, reduced to what reconciliation compares. */
export type ServerSnapshot = {
  order: 'missing' | { submitted: boolean }
  pools: {
    id: string
    items: { id: string; forwardFileId?: string; reverseFileId?: string; itemType?: 'SAMPLE' | 'NEGATIVE_CONTROL'; hasPrimers?: boolean }[]
  }[]
  /** Uploaded and verified files per pool id. */
  completed: Record<string, { id: string; fileName: string }[]>
  /** The settings the wizard lets the lab change, as the order holds them. */
  settings?: {
    targetTaxonomy: TargetTaxonomyKey
    processingType: ProcessingTypeKey
    allowSingletons: boolean
    targetRegion?: TargetRegionKey
    sampleConfiguration?: SampleConfigurationTypeKey
  }
}

const IN_CLASSIC = 'changed in the classic view'

/**
 * The order's settings over the draft's, unless the draft has a change of its own still to write: a
 * change made in the classic view sticks and is named.
 */
const reconcileSettings = (draft: DraftState, server: ServerSnapshot): Pick<DraftState, 'settings' | 'changedInClassic'> => {
  const { settings } = draft
  const held = server.settings
  if (!settings || !held || draft.settingsWrite) {
    return { settings, changedInClassic: draft.changedInClassic }
  }
  const changed: SettingChange['setting'][] = []
  let next: PlannedOrderSettings = settings
  if (held.targetTaxonomy !== settings.targetTaxonomy.value) {
    changed.push('targetTaxonomy')
    next = {
      ...next,
      targetTaxonomy: { value: held.targetTaxonomy, source: 'entered', reason: IN_CLASSIC },
      processingTypeConflict: undefined
    }
    next = { ...next, targetRegion: regionOf(next.targetTaxonomy, next.targetRegionEntered) }
  }
  /* the classic view shows a CO1 order without a region as worldwide */
  const heldRegion = held.targetTaxonomy === 'CO1_ANIMAL' ? (held.targetRegion ?? 'WORLDWIDE') : undefined
  if (heldRegion && heldRegion !== next.targetRegion) {
    changed.push('targetRegion')
    next = { ...next, targetRegion: heldRegion, targetRegionEntered: heldRegion }
  }
  if (held.processingType !== settings.processingType.value) {
    changed.push('processingType')
    next = { ...next, processingType: { value: held.processingType, source: 'entered', reason: IN_CLASSIC }, processingTypeConflict: undefined }
  }
  if (held.sampleConfiguration && held.sampleConfiguration !== settings.sampleConfiguration.value) {
    changed.push('sampleConfiguration')
    next = { ...next, sampleConfiguration: { value: held.sampleConfiguration, source: 'entered', reason: IN_CLASSIC } }
  }
  if (held.allowSingletons !== settings.allowSingletons) {
    changed.push('allowSingletons')
    next = { ...next, allowSingletons: held.allowSingletons, allowSingletonsSource: 'entered' }
  }
  const all = [...new Set([...(draft.changedInClassic ?? []), ...changed])]
  return { settings: next, changedInClassic: all.length > 0 ? all : undefined }
}

export type DropReason = 'order-missing' | 'order-submitted'

export type ReconcileOutcome = { kind: 'keep'; draft: DraftState } | { kind: 'drop'; reason: DropReason }

type ServerItem = ServerSnapshot['pools'][number]['items'][number] & { poolId: string }

/**
 * Pool ids stay positional, since a group's `pool` indexes them: a pool the server lost leaves a hole the
 * pool moves fill again, and a server pool the draft never knew (made in the classic view) is appended.
 */
const reconcilePoolIds = (draft: DraftState, server: ServerSnapshot): string[] => {
  const serverPoolIds = server.pools.map((pool) => pool.id)
  const poolIds = draft.poolIds.map((id) => (serverPoolIds.includes(id) ? id : ''))
  const unknown = serverPoolIds.filter((id) => !poolIds.includes(id))
  if (!poolIds[0] && unknown.length > 0) {
    poolIds[0] = unknown.shift()!
  }
  return [...poolIds, ...unknown]
}

/**
 * A restored draft brought in line with its order. The server wins every conflict, so changes made in the
 * classic view stick; an item deleted there is not created again while its file is on the order, and
 * uploads the server does not know have to be added again.
 */
export const reconcileDraft = (draft: DraftState, server: ServerSnapshot): ReconcileOutcome => {
  if (server.order === 'missing') {
    return { kind: 'drop', reason: 'order-missing' }
  }
  if (server.order.submitted) {
    return { kind: 'drop', reason: 'order-submitted' }
  }

  const poolIds = reconcilePoolIds(draft, server)
  const completedByName = new Map(
    Object.values(server.completed).flatMap((files) => files.map((file) => [file.fileName, file.id] as const))
  )
  const files: Record<string, WizardFile> = {}
  for (const [key, file] of Object.entries(draft.files)) {
    if (!file.upload) {
      files[key] = file
      continue
    }
    const serverFileId = completedByName.get(file.name)
    if (serverFileId) {
      files[key] = { ...file, upload: { status: 'uploaded', progress: 1, serverFileId } }
    } else if (file.upload.status === 'uploaded' || file.upload.status === 'uploading' || file.upload.status === 'pending') {
      files[key] = { ...file, upload: { status: 'needs-readd', progress: 0 } }
    } else {
      files[key] = file
    }
  }

  const itemsById = new Map<string, ServerItem>()
  const itemsByFileId = new Map<string, ServerItem[]>()
  for (const pool of server.pools) {
    for (const item of pool.items) {
      const located = { ...item, poolId: pool.id }
      itemsById.set(item.id, located)
      for (const fileId of new Set([item.forwardFileId, item.reverseFileId])) {
        if (fileId) {
          itemsByFileId.set(fileId, [...(itemsByFileId.get(fileId) ?? []), located])
        }
      }
    }
  }

  const fileNames = new Map(Object.values(server.completed).flatMap((pool) => pool.map((file) => [file.id, file.fileName] as const)))
  const keyOfFileId = (serverFileId: string) => Object.values(files).find((file) => file.upload?.serverFileId === serverFileId)?.key
  let releasedFiles = draft.releasedFiles?.filter((entry) => fileNames.has(entry.serverFileId))
  const fileOrder = [...draft.fileOrder]

  /* the R2 the server holds for the item, as the draft knows the file, or as a file known by name only */
  const reverseKeyFor = (serverFileId: string, platform: WizardFile['platform']): string | undefined => {
    const known = keyOfFileId(serverFileId)
    if (known) {
      return known
    }
    const name = fileNames.get(serverFileId)
    if (!name) {
      return undefined
    }
    const key = `server:${serverFileId}`
    files[key] = {
      name,
      size: 0,
      lastModified: 0,
      key,
      kind: 'fastq',
      fromServer: true,
      ...(platform ? { platform } : {}),
      upload: { status: 'uploaded', progress: 1, serverFileId }
    }
    fileOrder.push(key)
    releasedFiles = releasedFiles?.filter((entry) => entry.serverFileId !== serverFileId)
    return key
  }

  /** The group's R2 as the server holds it; an R2 joined in the wizard and not yet attached stays. */
  const reverseOf = (group: WizardSampleGroup, item: ServerItem): Partial<WizardSampleGroup> => {
    const held = group.reverse ? files[group.reverse]?.upload?.serverFileId : undefined
    if (item.reverseFileId && item.reverseFileId === held) {
      return { reverse: group.reverse, reverseAttach: undefined }
    }
    if (item.reverseFileId) {
      const reverse = reverseKeyFor(item.reverseFileId, files[group.forward]?.platform)
      return reverse ? { reverse, reverseAttach: undefined, reverseFromClassic: true } : {}
    }
    if (!group.reverse || group.reverseAttach) {
      return {}
    }
    const file = files[group.reverse]
    if (held && file && !releasedFiles?.some((entry) => entry.serverFileId === held)) {
      releasedFiles = [...(releasedFiles ?? []), { name: file.name, ...(file.fromServer ? {} : { size: file.size }), serverFileId: held }]
    }
    delete files[group.reverse]
    return { reverse: undefined, reverseAttach: undefined, reverseFromClassic: true }
  }

  const serverReverses = new Set([...itemsById.values()].flatMap((item) => (item.reverseFileId ? [item.reverseFileId] : [])))
  const groups: Record<string, WizardSampleGroup> = {}
  for (const [key, stored] of Object.entries(draft.groups)) {
    const group = withoutInFlightWrites(stored)
    const fileId = files[group.forward]?.upload?.serverFileId
    /* an item holding the file as its R2 is another group's: this one was absorbed into it */
    const candidates = fileId ? (itemsByFileId.get(fileId) ?? []).filter((candidate) => candidate.forwardFileId === fileId) : []
    const own = group.item.itemId ? itemsById.get(group.item.itemId) : undefined
    const item = own && candidates.includes(own) ? own : candidates[0]
    const archiveItems = group.archiveItems
      ? Object.fromEntries(
          Object.entries(group.archiveItems)
            .filter(([, archiveItem]) => candidates.some((candidate) => candidate.id === archiveItem.itemId))
            .map(([sampleKey, archiveItem]) => [sampleKey, { ...archiveItem, itemType: itemsById.get(archiveItem.itemId)?.itemType }])
        )
      : undefined
    if (item) {
      const pool = poolIds.indexOf(item.poolId)
      const typeChanged = !!item.itemType && item.itemType !== group.itemType
      groups[key] = {
        ...group,
        ...reverseOf(group, item),
        archiveItems,
        item: { status: 'created', itemId: item.id, poolId: item.poolId, itemType: item.itemType },
        itemType: item.itemType ?? group.itemType,
        typeSource: typeChanged ? 'user' : group.typeSource,
        primersOnServer: !!item.hasPrimers,
        pool: pool > 0 ? pool : undefined
      }
    } else if (fileId && serverReverses.has(fileId)) {
      continue
    } else if (fileId && (group.item.status === 'created' || group.item.status === 'removed')) {
      groups[key] = { ...group, archiveItems, item: { status: 'removed' } }
    } else {
      groups[key] = { ...group, archiveItems, item: { status: 'waiting' } }
    }
  }

  return {
    kind: 'keep',
    draft: {
      ...draft,
      ...reconcileSettings(draft, server),
      poolIds,
      files,
      fileOrder: fileOrder.filter((key) => !!files[key]),
      groups,
      releasedFiles: releasedFiles && releasedFiles.length > 0 ? releasedFiles : undefined,
      phase: draft.orderId ? 'active' : 'start',
      error: undefined
    }
  }
}
